import ShieldAlert from "@redotech/redo-icons/alert-circle.svg";
import Archive from "@redotech/redo-icons/archive.svg";
import ArrowLeft from "@redotech/redo-icons/arrow-left.svg";
import CreditCard from "@redotech/redo-icons/credit-card-01.svg";
import Mail from "@redotech/redo-icons/mail-01.svg";
import BotMessageSquare from "@redotech/redo-icons/message-chat-circle.svg";
import MessageSquareText from "@redotech/redo-icons/message-text-square-02.svg";
import PenLine from "@redotech/redo-icons/pencil-01.svg";
import Radio from "@redotech/redo-icons/radio.svg";
import RotateCcw from "@redotech/redo-icons/reverse-left_filled.svg";
import Shapes from "@redotech/redo-icons/shapes.svg";
import BrainCircuit from "@redotech/redo-icons/stars-01.svg";
import Truck from "@redotech/redo-icons/truck-01.svg";

import type { FirstInstallOnboardingProduct } from "@redotech/redo-model/onboarding";
import type { ElementType } from "react";

export interface FirstInstallProductHero {
  headline: string;
  bullets: readonly [string, string, string];
}

export interface FirstInstallProductChoice {
  id: FirstInstallOnboardingProduct;
  label: string;
  subtext: string;
  badges?: readonly string[];
  description: string;
  setupKind: "self-serve" | "guided";
  Icon: ElementType<{ className?: string }>;
  hero: FirstInstallProductHero;
}

const FIRST_INSTALL_OTHER_PRODUCT_IDS = new Set<FirstInstallOnboardingProduct>([
  "contextual",
  "aeo",
  "checkout-optimization",
  "inventory-management",
]);

export const FIRST_INSTALL_PRODUCT_CHOICES: FirstInstallProductChoice[] = [
  {
    id: "returns",
    label: "Returns",
    subtext: "15 min setup",
    description:
      "Keep more revenue with AI-guided returns, exchanges, and claims.",
    setupKind: "self-serve",
    Icon: RotateCcw,
    hero: {
      headline: "Convert more returns into exchanges",
      bullets: [
        "AI exchange recommendations",
        "Automated return and claim reviews",
        "Product insights to reduce repeat returns",
      ],
    },
  },
  {
    id: "chargebacks",
    label: "Chargebacks",
    subtext: "5 min setup",
    badges: ["Free"],
    description:
      "Recover revenue with AI evidence and unified dispute management.",
    setupKind: "self-serve",
    Icon: ShieldAlert,
    hero: {
      headline: "Win more chargebacks with less manual work",
      bullets: [
        "55-70% average win rate",
        "Evidence from orders, tracking, and communications",
        "Shopify, Stripe, and PayPal in one view",
      ],
    },
  },
  {
    id: "order-editing",
    label: "Order Editing",
    subtext: "10 min setup",
    badges: ["Free"],
    description: "Let shoppers fix orders before fulfillment starts.",
    setupKind: "self-serve",
    Icon: PenLine,
    hero: {
      headline: "Prevent tickets before orders ship",
      bullets: [
        "Self-service edits from confirmation pages",
        "Order holds for 3PL and ERP flows",
        "Editable rules, cutoffs, and cancellations",
      ],
    },
  },
  {
    id: "shipping-fulfillment",
    label: "Shipping & Fulfillment",
    subtext: "Schedule a call",
    description:
      "Reduce shipping mistakes and label costs with faster fulfillment workflows.",
    setupKind: "self-serve",
    Icon: Truck,
    hero: {
      headline: "Ship faster with fewer errors",
      bullets: [
        "Auto-batch similar orders",
        "Merge shipments to cut label costs",
        "Real-time Shopify and fulfillment sync",
      ],
    },
  },
  {
    id: "recover",
    label: "Recover",
    subtext: "7 min setup",
    description:
      "Bring back abandoned carts that automated flows cannot reach.",
    setupKind: "self-serve",
    Icon: MessageSquareText,
    hero: {
      headline: "Recover carts automation misses",
      bullets: [
        "P2P-compliant human outreach",
        "Personal conversations that bring shoppers back",
        "Pay only when Recover drives revenue",
      ],
    },
  },
  {
    id: "contextual",
    label: "Contextual",
    subtext: "Schedule a call",
    description: "Trigger relevant offers and messages from shopper behavior.",
    setupKind: "self-serve",
    Icon: Radio,
    hero: {
      headline: "Respond to shopper intent in real time",
      bullets: [
        "Behavior-based targeting",
        "Personalized offers and messages",
        "Context-aware campaign paths",
      ],
    },
  },
  {
    id: "email-sms",
    label: "Email & SMS",
    subtext: "Schedule a call",
    description:
      "Build persona-based campaigns with shoppable email and tailored SMS.",
    setupKind: "guided",
    Icon: Mail,
    hero: {
      headline: "Send messages shoppers can act on",
      bullets: [
        "AI personas from customer behavior",
        "Shoppable emails and tailored SMS",
        "Campaigns coordinated in one dashboard",
      ],
    },
  },
  {
    id: "aeo",
    label: "Answer Engine Optimization",
    subtext: "Schedule a call",
    description:
      "Shape how AI answer engines understand and recommend your brand.",
    setupKind: "guided",
    Icon: BrainCircuit,
    hero: {
      headline: "Help AI recommend your brand",
      bullets: [
        "Brand facts organized for answer engines",
        "Gaps and opportunities surfaced",
        "Guided setup with Redo",
      ],
    },
  },
  {
    id: "checkout-optimization",
    label: "Checkout Optimization",
    subtext: "Schedule a call",
    description:
      "Lift conversion and profit with personalized checkout tests and rates.",
    setupKind: "guided",
    Icon: CreditCard,
    hero: {
      headline: "Optimize the checkout that decides profit",
      bullets: [
        "Continuous AI-backed A/B tests",
        "Personalized offers and shipping rates",
        "Revenue impact tracked in real time",
      ],
    },
  },
  {
    id: "ai-sales-support",
    label: "AI Sales + Support",
    subtext: "Schedule a call",
    description:
      "Unify support channels and give shoppers AI-assisted 1:1 help.",
    setupKind: "guided",
    Icon: BotMessageSquare,
    hero: {
      headline: "Answer faster across every channel",
      bullets: [
        "AI drafted or automated replies",
        "One inbox for support conversations",
        "Sales agent that recommends products",
      ],
    },
  },
  {
    id: "inventory-management",
    label: "Inventory Management",
    subtext: "Schedule a call",
    description:
      "Forecast demand, plan purchase orders, and keep every channel in sync.",
    setupKind: "guided",
    Icon: Archive,
    hero: {
      headline: "Keep the right stock in the right place",
      bullets: [
        "Forecasts tuned to sales and seasonality",
        "Suggested purchase orders",
        "One source of truth for every SKU",
      ],
    },
  },
];

export const FIRST_INSTALL_PRIMARY_PRODUCT_CHOICES =
  FIRST_INSTALL_PRODUCT_CHOICES.filter(
    (choice) => !FIRST_INSTALL_OTHER_PRODUCT_IDS.has(choice.id),
  );

export const FIRST_INSTALL_OTHER_PRODUCT_CHOICES =
  FIRST_INSTALL_PRODUCT_CHOICES.filter((choice) =>
    FIRST_INSTALL_OTHER_PRODUCT_IDS.has(choice.id),
  );

export const FIRST_INSTALL_OTHER_PRODUCT_OPTION = {
  value: "other",
  label: "Other",
  Icon: Shapes,
};

export const FIRST_INSTALL_BACK_PRODUCT_OPTION = {
  value: "back",
  label: "Back",
  Icon: ArrowLeft,
};
